import {
	CoverageBar,
	CoveragePercentage,
	CoverageStatusLabel,
	type CoverageValue,
} from '../../Coverage/Coverage';
import { SelectionCheckbox } from '../../SelectionCheckbox/SelectionCheckbox';
import styles from './SimpleDataTable.module.css';

export type SimpleDataTableCellVariant = 'text' | 'coverage' | 'status';
export type SimpleDataTableCellEmphasis = 'strong' | 'default' | 'muted';
export type SimpleDataTableCoverageValue = CoverageValue;

export interface SimpleDataTableColumn {
	key: string;
	label: string;
	variant?: SimpleDataTableCellVariant;
	emphasis?: SimpleDataTableCellEmphasis;
	align?: 'left' | 'right';
}

export type SimpleDataTableCellTone = 'positive' | 'negative';

export interface SimpleDataTableRow {
	id: string;
	cells: Record<string, string>;
	coverage?: Record<string, SimpleDataTableCoverageValue>;
	cellTones?: Record<string, SimpleDataTableCellTone>;
	isTotal?: boolean;
}

export interface SimpleDataTableSelection {
	selectedRowIds: string[];
	onToggleRow: (rowId: string) => void;
	onToggleAll: () => void;
}

export interface SimpleDataTableProps {
	columns: SimpleDataTableColumn[];
	rows: SimpleDataTableRow[];
	minWidth?: string;
	selection?: SimpleDataTableSelection;
	coverageLayout?: 'fill' | 'compact';
}

const CoverageCell = ({ value }: { value?: SimpleDataTableCoverageValue }) => {
	if (!value) return <>—</>;

	return (
		<div className={styles.coverageCell}>
			<CoverageBar percentage={value.percentage} />
			<CoveragePercentage value={value} />
		</div>
	);
};

const StatusCell = ({ value }: { value?: SimpleDataTableCoverageValue }) => {
	if (!value) return <>—</>;

	return <CoverageStatusLabel status={value.status} />;
};

const renderCellContent = (
	variant: SimpleDataTableCellVariant,
	row: SimpleDataTableRow,
	columnKey: string
) => {
	if (variant === 'coverage') return <CoverageCell value={row.coverage?.[columnKey]} />;
	if (variant === 'status') return <StatusCell value={row.coverage?.[columnKey]} />;
	return row.cells[columnKey] ?? '—';
};

const emphasisClass = (emphasis?: SimpleDataTableCellEmphasis) => {
	if (emphasis === 'strong') return styles.emphasisStrong;
	if (emphasis === 'muted') return styles.emphasisMuted;
	return '';
};

const TONE_CLASS: Record<SimpleDataTableCellTone, string> = {
	positive: styles.tonePositive,
	negative: styles.toneNegative,
};

const rowClassName = (row: SimpleDataTableRow, isSelected: boolean) => {
	if (row.isTotal) return styles.totalRow;
	return isSelected ? `${styles.dataRow} ${styles.selectedRow}` : styles.dataRow;
};

export const SimpleDataTable = ({
	columns,
	rows,
	minWidth,
	selection,
	coverageLayout = 'fill',
}: SimpleDataTableProps) => {
	const selectableIds = rows.filter((row) => !row.isTotal).map((row) => row.id);
	const selectedCount = selection
		? selectableIds.filter((id) => selection.selectedRowIds.includes(id)).length
		: 0;
	const isAllSelected = selectableIds.length > 0 && selectedCount === selectableIds.length;
	const hasCoverage = columns.some((column) => column.variant === 'coverage');
	const coverageClass = coverageLayout === 'compact' ? styles.compactCoverage : styles.fillCoverage;

	return (
		<div className={styles.wrapper}>
			<table
				className={`${styles.table} ${hasCoverage ? coverageClass : ''}`}
				style={minWidth ? { minWidth } : undefined}
			>
				<thead>
					<tr>
						{selection && (
							<th className={styles.selectionCell}>
								<SelectionCheckbox
									checked={isAllSelected}
									indeterminate={selectedCount > 0 && !isAllSelected}
									onChange={selection.onToggleAll}
									ariaLabel="Selecionar todos"
								/>
							</th>
						)}
						{columns.map((column) => (
							<th
								key={column.key}
								className={[
									styles.headerCell,
									column.align === 'right' ? styles.alignRight : '',
									column.variant === 'coverage' ? styles.coverageHeaderCell : '',
									column.variant === 'status' ? styles.statusCell : '',
								]
									.filter(Boolean)
									.join(' ')}
							>
								{column.label}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((row) => {
						const isSelected = !!selection?.selectedRowIds.includes(row.id);
						return (
							<tr key={row.id} className={rowClassName(row, isSelected)}>
								{selection && (
									<td
										className={
											row.isTotal
												? `${styles.totalCell} ${styles.selectionCell}`
												: `${styles.bodyCell} ${styles.selectionCell}`
										}
									>
										{!row.isTotal && (
											<SelectionCheckbox
												checked={isSelected}
												onChange={() => selection.onToggleRow(row.id)}
												ariaLabel={`Selecionar ${row.cells[columns[0]?.key] ?? row.id}`}
											/>
										)}
									</td>
								)}
								{columns.map((column) => {
									const variant = column.variant ?? 'text';
									const tone = row.cellTones?.[column.key];
									const baseClassName = row.isTotal
										? styles.totalCell
										: [
												styles.bodyCell,
												emphasisClass(column.emphasis),
												variant === 'coverage' ? styles.coverageBodyCell : '',
												variant === 'status' ? styles.statusCell : '',
												column.align === 'right' ? styles.alignRight : '',
											]
												.filter(Boolean)
												.join(' ');
									const className = tone ? `${baseClassName} ${TONE_CLASS[tone]}` : baseClassName;

									return (
										<td key={`${row.id}-${column.key}`} className={className}>
											{renderCellContent(variant, row, column.key)}
										</td>
									);
								})}
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
};
